import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { BankCounterLineFormGroup, BankFeeFormGroup } from './bank-transaction-form';

/**
 * Which situation the shared movement form is rendering (#5089).
 *
 * <p>One enum rather than a handful of independent booleans. The four values are mutually exclusive real
 * situations, so a caller cannot assemble a combination that does not exist — an instrument on a receipt
 * confirmation, say, or a "Money in" title over a leg that is sending. Everything the form varies (labels,
 * whether charges come out of what arrives or add to what leaves, which sections appear) is derived from
 * this one input.</p>
 */
export type BankMovementMode = 'movement-in' | 'movement-out' | 'transfer-send' | 'transfer-receive';

/**
 * How the movement was executed. Absent from <see cref="BankMovementControls"/> when the mode has no
 * instrument to record — a receipt confirmation reports what a bank did TO us.
 */
export interface BankInstrumentControls {
  type: FormControl<number>;
  endToEndId: FormControl<string | null>;
  uetr: FormControl<string | null>;
  chargeBearer: FormControl<string | null>;
  traceNumber: FormControl<string | null>;
}

/**
 * The GL accounts on the other side, as an editable breakdown.
 *
 * <p>Omitted entirely when the document supplies the other side rather than the user picking it — an
 * internal transfer always posts against Cash in Transit. That absence is also what enforces the API's
 * rule that only a hand-entered movement may split its counter side: a transfer leg has no control to
 * add a row to, so the shape makes the invalid case unreachable rather than guarding against it.</p>
 */
export type CounterAccountControls = FormArray<FormGroup<BankCounterLineFormGroup>>;

/**
 * The controls the shared form drives.
 *
 * <p>A plain object of controls, not a <c>FormGroup</c>: the three host screens each keep their own form
 * with their own extra fields and their own names (<c>transferDate</c> here, <c>receivedOn</c> there), and
 * this lets each map its controls onto one shape without renaming anything or fighting FormGroup's
 * generics. Optional keys are how a mode says it has no such section — structure instead of a flag.</p>
 */
export interface BankMovementControls {
  /** Gross for every mode except `transfer-receive`, where it is what actually landed. */
  amount: FormControl<number | null>;
  date: FormControl<Date | null>;

  /** What the bank's own statement line says — the field reconciliation matches on. */
  statementReference?: FormControl<string | null>;

  /** Free text describing what the movement was for. */
  memo?: FormControl<string | null>;

  instrument?: BankInstrumentControls;
  counterAccount?: CounterAccountControls;

  fees: FormArray<FormGroup<BankFeeFormGroup>>;
}

/** One line of the entry as it will be posted, for the live preview. */
export interface PreviewLine {
  accountNumber: string | null;
  accountName: string;
  debit: number;
  credit: number;

  /**
   * The line's own currency, when the entry has more than one.
   *
   * <p>Omitted on an ordinary entry, where every line is in the movement's currency and repeating it on
   * each row is noise. Set on a converted internal transfer, where cash in transit is credited in the money
   * that left and the bank is debited in the money that arrived — two figures a reader would otherwise
   * assume were comparable.</p>
   */
  currency?: string;
}

/**
 * True when charges come out of the money as it arrives, rather than being added to what leaves.
 *
 * <p>Mirrors <c>BankPostingHelper.FeesDeductedOnArrival</c> on the API. Receiving 10,000 with 24 in charges
 * puts 9,976 in the bank; paying 10,000 with 24 in charges takes 10,024 out of it. Getting this backwards
 * produces a net that looks plausible and is wrong by twice the fees.</p>
 */
export function feesDeductedOnArrival(mode: BankMovementMode): boolean {
  return mode === 'movement-in' || mode === 'transfer-receive';
}

/**
 * What actually moves the bank balance: the gross adjusted by the charges, in whichever direction this
 * mode takes them.
 *
 * <p>Shared because the form renders it as the net row while each host needs the same figure for the bank
 * line of its entry preview, and the two disagreeing would put a number on screen that the preview beside
 * it contradicts.</p>
 */
export function netMovementAmount(mode: BankMovementMode, grossAmount: number, totalFees: number): number {
  return feesDeductedOnArrival(mode) ? grossAmount - totalFees : grossAmount + totalFees;
}
